import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const SubscriptionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    product_id: '',
    custom_alias: '',
    start_date: new Date().toISOString().split('T')[0],
    next_renewal_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    sales_date: new Date().toISOString().split('T')[0],
    service_details: '',
    quotation_id: '',
    quotations: [],
    quantity: 1,
    amount: 0,
    subtotal: 0,
    tax_percent: 0,
    tax_amount: 0,
    status: 'active',
    notes: '',
  });
  const [subscription, setSubscription] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [expiryChanged, setExpiryChanged] = useState(false);
  const [renewalToggled, setRenewalToggled] = useState(false);
  const [originalRenewalDate, setOriginalRenewalDate] = useState('');
  const [originalIsRenewal, setOriginalIsRenewal] = useState(false);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [serviceDetailsEditMode, setServiceDetailsEditMode] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachQuotationMandatory, setAttachQuotationMandatory] = useState(true);

  const addMonths = (dateStr, months) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + parseInt(months || 0));
    return d.toISOString().split('T')[0];
  };

  const resolveQuoteTenureMonths = (quotationId, selQuote) => {
    const q = selQuote || (quotationId && quotes.find(q => q._id === quotationId)) || null;
    if (!q) return 0;
    const firstItem = q.items?.[0] || {};
    const tenure = parseInt(firstItem.tenure || firstItem.tenureMonths || firstItem.durationMonths || q.tenureMonths || q.tenure || 0);
    return tenure || 0;
  };

  useEffect(() => {
    fetchInitialData();
    if (isEdit) fetchSubscription();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [custRes, prodRes, quoteRes] = await Promise.all([
        billingService.getCustomers({ limit: 5000 }),
        billingService.getProducts(),
        billingService.getQuotes({})
      ]);
      if (custRes.success) setCustomers(custRes.data);
      if (prodRes.success) setProducts(prodRes.data);
      if (quoteRes.success) setQuotes(quoteRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await billingService.getSubscriptionById(id);
      if (res.success) {
        setSubscription(res.data);
        const nextRenewalDate = new Date(res.data.next_renewal_date).toISOString().split('T')[0];
        setOriginalRenewalDate(nextRenewalDate);
        setAttachments(Array.isArray(res.data.attachments) ? res.data.attachments : []);
        setFormData({
          ...res.data,
          start_date: new Date(res.data.start_date).toISOString().split('T')[0],
          next_renewal_date: nextRenewalDate,
          sales_date: res.data.sales_date ? new Date(res.data.sales_date).toISOString().split('T')[0] : '',
        });
        
        if (res.data.customer_id) {
          const customer = res.data.customer_id;
          if (typeof customer === 'object') {
            setCustomerSearch(`${customer.firstname} ${customer.lastname}`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  useEffect(() => {
    if (isEdit && subscription && customers.length > 0) {
      const customerId = typeof subscription.customer_id === 'object' ? subscription.customer_id._id : subscription.customer_id;
      const customer = customers.find(c => c._id === customerId);
      if (customer) {
        setCustomerSearch(`${customer.firstname} ${customer.lastname}`);
      }
    }
  }, [isEdit, subscription, customers]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.position-relative')) {
        setShowCustomerDropdown(false);
        setShowQuoteDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getHundredYearsLater = (baseDate) => {
    const date = baseDate ? new Date(baseDate) : new Date();
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    date.setFullYear(date.getFullYear() + 100);
    return date.toISOString().split('T')[0];
  };

  const isNonRenewalProduct = (product) => {
    if (!product) return false;
    const renewable = product.isRenewable ?? product.isRenewal ?? product.isRewal ?? true;
    return renewable === false;
  };

  const getSelectedProduct = () => products.find((p) => p._id === formData.product_id);

  const handleProductChange = (productId) => {
    const product = products.find(p => p._id === productId);
    if (product) {
      const updatedFormData = {
        ...formData,
        product_id: productId,
        amount: product.sellingPrice
      };
      if (!updatedFormData.custom_alias || !updatedFormData.custom_alias.trim()) {
        updatedFormData.custom_alias = product.productName || '';
      }

      if (isNonRenewalProduct(product)) {
        updatedFormData.next_renewal_date = getHundredYearsLater(formData.start_date);
      }

      setFormData(updatedFormData);
    }
  };

  const handleQuoteChange = (quoteId) => {
    if (!quoteId) return;
    
    const quote = quotes.find(q => q._id === quoteId);
    if (quote) {
      if (quote.items && quote.items.length > 1) {
        alert('This quotation has multiple items. Please select a quotation with a single service/product.');
        return;
      }

      const updatedQuotes = [...(formData.quotations || [])];
      if (!updatedQuotes.includes(quoteId)) {
        updatedQuotes.push(quoteId);
      }

      const firstItem = quote.items?.[0];
      const productId = firstItem?.productId || formData.product_id;
      const product = products.find((p) => p._id === productId);
      const itemAlias = firstItem?.description || firstItem?.customAlias || firstItem?.productName || product?.productName || '';
      const updatedFormData = {
        ...formData,
        quotation_id: quoteId,
        quotations: updatedQuotes,
        amount: quote.totalAmount,
        product_id: productId,
        custom_alias: formData.custom_alias && formData.custom_alias.trim() ? formData.custom_alias : itemAlias
      };

      const tenureMonths = firstItem?.tenure || firstItem?.tenureMonths || 0;
      const lockedExpiry = isNonRenewalProduct(product);
      if (lockedExpiry) {
        updatedFormData.next_renewal_date = getHundredYearsLater(formData.start_date);
      } else if (tenureMonths > 0 && !expiryChanged) {
        updatedFormData.next_renewal_date = addMonths(formData.start_date, tenureMonths);
      }

      setFormData(updatedFormData);
      setSelectedQuote(quote);
      setExpiryChanged(false);
      setQuoteSearch(quote.quotationNumber);
      setShowQuoteDropdown(false);
    }
  };

  const handleExpiryChange = (date) => {
    setFormData({ ...formData, next_renewal_date: date });
    if (isEdit && date !== originalRenewalDate) {
      setExpiryChanged(true);
    } else {
      setExpiryChanged(false);
    }
  };

  const handleAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';

    setUploadingAttachments(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const res = await customerService.uploadFile(formData);
          if (res && res.success && res.data && res.data.length > 0) {
            const item = res.data[0];
            uploaded.push({
              _id: item._id,
              file_name: item.fileName,
              file_size: item.fileSize,
              file_type: item.fileType,
              s3Location: item.s3Location || item.url,
              localPath: item.localPath,
              url: item.url || item.s3Location,
              uploaded_at: item.uploadedAt || new Date().toISOString()
            });
          }
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          alert(`Could not upload ${file.name}. ${err.message || err}`);
        }
      }
      if (uploaded.length > 0) {
        setAttachments(prev => [...prev, ...uploaded]);
      }
    } finally {
      setUploadingAttachments(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentDownload = async (att) => {
    await customerService.downloadAttachment(att);
  };

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lastQuoteId = formData.quotation_id;
    
    const isNewQuoteAttached = isEdit 
      ? (lastQuoteId && !subscription.quotations?.some(qId => qId.toString() === lastQuoteId.toString())) 
      : !!lastQuoteId;

    const manualMode = !attachQuotationMandatory && !formData.quotation_id;

    try {
      setLoading(true);

      let payload = { ...formData };
      if (manualMode) {
        if (!payload.product_id) {
          alert('Please select a product.');
          setLoading(false);
          return;
        }
        const subtotal = parseFloat(payload.subtotal);
        const taxPercent = parseFloat(payload.tax_percent);
        if (Number.isNaN(subtotal) || subtotal <= 0) {
          alert('Please enter a valid Subtotal (greater than 0).');
          setLoading(false);
          return;
        }
        if (Number.isNaN(taxPercent) || taxPercent < 0) {
          alert('Please enter a valid Tax %.');
          setLoading(false);
          return;
        }
        const taxAmount = +(subtotal * taxPercent / 100).toFixed(2);
        const totalAmount = +(subtotal + taxAmount).toFixed(2);
        payload = {
          ...payload,
          subtotal,
          tax_percent: taxPercent,
          tax_amount: taxAmount,
          amount: totalAmount
        };
      }

      if (attachments.length > 0) {
        payload.attachments = attachments;
      }
      
      delete payload.new_comment;
      delete payload.comments;
      delete payload.history;
      delete payload._id;
      delete payload.isRenewal;
      delete payload.freeTicketSupport;
      delete payload.paidTicketSupport;
      if (isEdit) {
        if (formData.comment) {
          payload.comment = String(formData.comment);
        }
        const res = await billingService.updateSubscription(id, payload);
        if (res.success) {
          navigate(-1);
        }
      } else {
        if (attachQuotationMandatory && !formData.quotation_id) {
          alert('Please select a quotation to attach. Uncheck "Attach Quotation (Mandatory)" to proceed with manual sale.');
          setLoading(false);
          return;
        }
        const res = await billingService.createSubscription(payload);
       
        if (res.success) {
          navigate(-1);
        }
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const searchValue = customerSearch.toLowerCase();
    const customerText = `${c.firstname || ''} ${c.lastname || ''} ${c.company || ''}`.toLowerCase();
    const customerEmail = String(c.email || '').toLowerCase();
    const customerPhone = String(c.phone || '');

    return (
      customerText.includes(searchValue) ||
      customerEmail.includes(searchValue) ||
      customerPhone.includes(customerSearch)
    );
  });

  const selectedProduct = getSelectedProduct();
  const shouldLockExpiryDate = isNonRenewalProduct(selectedProduct);
  const getHistoryStatus = (historyItem) => (
    historyItem?.new_value?.status ||
    historyItem?.status ||
    historyItem?.old_value?.status ||
    subscription?.status ||
    'N/A'
  );

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
         
          <h4 className="fw-700 mb-0">{isEdit ? 'Edit Subscription' : 'New Subscription'}</h4>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-md-8">
          <form onSubmit={handleSubmit}>
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-md-6 position-relative">
                    <label className="form-label text-muted small fw-600">CUSTOMER</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                      <input 
                        type="text" 
                        className="form-control bg-light border-start-0" 
                        placeholder="Search customer..." 
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        disabled={isEdit}
                        required
                      />
                    </div>
                    {showCustomerDropdown && !isEdit && (
                      <div className="dropdown-menu show w-100 shadow-sm border-0 mt-1 overflow-auto" style={{ maxHeight: '300px', zIndex: 1000 }}>
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map(c => (
                            <button 
                              key={c._id} 
                              type="button" 
                              className="dropdown-item py-2" 
                              onClick={() => {
                                setFormData({...formData, customer_id: c._id});
                                setCustomerSearch(`${c.firstname} ${c.lastname}`);
                                setShowCustomerDropdown(false);
                              }}
                            >
                              <div className="fw-600">{c.firstname} {c.lastname} {c.company || ''}</div>
                              <div className="text-xs text-muted">{c.email} • {c.phone}</div>
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item disabled text-muted text-xs">No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-600">PRODUCT</label>
                    <select 
                      className="form-select" 
                      value={formData.product_id} 
                      onChange={e => handleProductChange(e.target.value)} 
                      required 
                      disabled={isEdit}
                    >
                      <option value="">Select Product...</option>
                      {products.map(p => <option key={p._id} value={p._id}>{p.productName}</option>)}
                    </select>
                  </div>
                  <div className="col-md-12">
                    <label className="form-label text-muted small fw-600">SERVICE ALIAS / NAME</label>
                    <input type="text" className="form-control" {...(isEdit && {disabled: true})} value={formData.custom_alias} onChange={e => setFormData({...formData, custom_alias: e.target.value})} placeholder="e.g. Main Website Hosting" />
                  </div>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch bg-light rounded-3 p-3 d-flex align-items-center gap-3 shadow-sm">
                    <input
                      className="form-check-input me-0"
                      type="checkbox"
                      role="switch"
                      id="attachQuotationMandatory"
                      checked={attachQuotationMandatory}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAttachQuotationMandatory(checked);
                        if (!checked) {
                          setFormData((prev) => ({
                            ...prev,
                            quotation_id: '',
                            subtotal: prev.amount ? parseFloat(prev.amount) : 0,
                            tax_percent: 0,
                            tax_amount: 0
                          }));
                          setQuoteSearch('');
                          setSelectedQuote(null);
                        }
                      }}
                      style={{ width: '2.2rem', height: '1.25rem' }}
                    />
                    <label className="form-check-label fw-700 d-flex flex-column" htmlFor="attachQuotationMandatory">
                      <span>ATTACH QUOTATION (MANDATORY)</span>
                      <span className="text-muted fw-400 text-xs">
                        {attachQuotationMandatory
                          ? 'Quotation selection is required to create this subscription (default flow).'
                          : 'Quotation is optional — enter Subtotal and Tax % manually below.'}
                      </span>
                    </label>
                    <span
                      className={`badge ms-auto ${attachQuotationMandatory ? 'bg-success' : 'bg-warning text-dark'}`}
                    >
                      {attachQuotationMandatory ? 'QUOTATION REQUIRED' : 'MANUAL SALE'}
                    </span>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6 position-relative">
                    <label className="form-label text-muted small fw-600">
                      ATTACH QUOTATION {isEdit && '(ADD NEW)'}
                      {!attachQuotationMandatory && (
                        <span className="badge bg-light text-dark border ms-2">Optional</span>
                      )}
                      {attachQuotationMandatory && (
                        <span className="badge bg-danger ms-2">Required</span>
                      )}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                      <input 
                        type="text" 
                        className="form-control bg-light border-start-0" 
                        placeholder="Search quote number..." 
                        value={quoteSearch}
                        onChange={(e) => {
                          setQuoteSearch(e.target.value);
                          setShowQuoteDropdown(true);
                        }}
                        onFocus={() => setShowQuoteDropdown(true)}
                        disabled={!formData.customer_id}
                      />
                    </div>
                    {showQuoteDropdown && formData.customer_id && (
                      <div className="dropdown-menu show w-100 shadow-sm border-0 mt-1 overflow-auto" style={{ maxHeight: '300px', zIndex: 1000 }}>
                        {(() => {
                          const term = quoteSearch.trim().toLowerCase();
                          const filtered = quotes.filter(q => {
                            if (q.customerId !== formData.customer_id) return false;
                            if (!(q.status === 'approved' || q.status === 'active')) return false;
                            if (q.items && q.items.length !== 1) return false;
                            if (!term) return true;
                            const number = String(q.quotationNumber || '').toLowerCase();
                            const customer = String(q.customerName || '').toLowerCase();
                            const company = String(q.customerCompany || '').toLowerCase();
                            const product = (q.items || []).map(item => String(item.productName || '').toLowerCase()).join(' ');
                            const amount = String(q.totalAmount || '');
                            return (
                              number.includes(term) ||
                              customer.includes(term) ||
                              company.includes(term) ||
                              product.includes(term) ||
                              amount.includes(term)
                            );
                          }).slice().sort((a, b) => {
                            const dA = new Date(a.createdAt || a.quotationDate || 0).getTime();
                            const dB = new Date(b.createdAt || b.quotationDate || 0).getTime();
                            return dB - dA;
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="dropdown-item disabled text-muted text-xs">
                                No approved single-item quotes found{quoteSearch.trim() ? ` matching "${quoteSearch.trim()}"` : ''}
                              </div>
                            );
                          }

                          return filtered.map(q => (
                            <button 
                              key={q._id} 
                              type="button" 
                              className="dropdown-item py-2" 
                              onClick={() => handleQuoteChange(q._id)}
                            >
                              <div className="d-flex justify-content-between align-items-start w-100">
                                <div className="fw-600">{q.quotationNumber}</div>
                                <span className="text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
                                  <i className="bi bi-calendar3 me-1"></i>
                                  {formatDate(q.createdAt || q.quotationDate)}
                                </span>
                              </div>
                              <div className="text-xs text-muted">
                                {q.items?.[0]?.productName || 'No Product'} • {formatCurrency(q.totalAmount)}
                                {q.customerName ? ` • ${q.customerName}` : ''}
                                {q.customerCompany ? ` (${q.customerCompany})` : ''}
                              </div>
                            </button>
                          ));
                        })()}
                      </div>
                    )}
                    <p className="text-muted text-xs mt-1 mb-0">Only approved single-item quotations for this customer are shown.</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-600">BILLING SUMMARY</label>
                    <div className="p-2 bg-light rounded-3 d-flex justify-content-between align-items-center" style={{ height: '38px' }}>
                      <span className="text-xs text-muted">Total Amount:</span>
                      <span className="fw-700 text-primary">{formatCurrency(formData.amount)}</span>
                    </div>
                  </div>
                </div>

                {!attachQuotationMandatory && !formData.quotation_id && (
                  <div className="card border border-warning-subtle bg-warning-light mb-4 shadow-sm">
                    <div className="card-header bg-transparent border-bottom border-warning-subtle pt-3 px-4 d-flex align-items-center justify-content-between">
                      <h6 className="fw-700 mb-0 text-warning-dark">
                        <i className="bi bi-pencil-square me-2"></i>MANUAL SALE — Subtotal &amp; Tax
                      </h6>
                      <span className="text-xs text-muted">No quotation attached</span>
                    </div>
                    <div className="card-body p-4">
                      <div className="row g-3 mb-3">
                        <div className="col-md-4">
                          <label className="form-label text-muted small fw-600">SUBTOTAL (₹) <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control form-control-lg fw-700"
                            placeholder="0.00"
                            value={formData.subtotal ?? 0}
                            onChange={(e) => {
                              const sub = parseFloat(e.target.value) || 0;
                              const taxPct = parseFloat(formData.tax_percent) || 0;
                              const taxAmt = +(sub * taxPct / 100).toFixed(2);
                              setFormData({
                                ...formData,
                                subtotal: sub,
                                tax_amount: taxAmt,
                                amount: +(sub + taxAmt).toFixed(2)
                              });
                            }}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small fw-600">TAX % <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control form-control-lg"
                            placeholder="e.g. 18 for 18%"
                            value={formData.tax_percent ?? 0}
                            onChange={(e) => {
                              const sub = parseFloat(formData.subtotal) || 0;
                              const taxPct = parseFloat(e.target.value) || 0;
                              const taxAmt = +(sub * taxPct / 100).toFixed(2);
                              setFormData({
                                ...formData,
                                tax_percent: taxPct,
                                tax_amount: taxAmt,
                                amount: +(sub + taxAmt).toFixed(2)
                              });
                            }}
                          />
                          <div className="text-xs text-muted mt-1 fw-500">Applies on Subtotal</div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small fw-600">TAX AMOUNT (₹)</label>
                          <div className="p-2 bg-light rounded-3 d-flex justify-content-between align-items-center" style={{ height: 'calc(1.5em + 1rem + 2px)' }}>
                            <span className="text-xs text-muted">Computed:</span>
                            <span className="fw-700 text-danger">{formatCurrency(formData.tax_amount || 0)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-4 offset-md-4">
                          <div className="p-3 bg-light rounded-3">
                            <div className="text-xs text-muted fw-600 mb-1">GRAND TOTAL</div>
                            <div className="text-primary fw-800" style={{ fontSize: '1.6rem', lineHeight: 1 }}>
                              {formatCurrency(formData.amount || 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {formData.quotations?.length > 0 && (
                  <div className="mb-4">
                    <label className="form-label text-muted small fw-600">ATTACHED QUOTATIONS</label>
                    <div className="d-flex flex-wrap gap-2">
                      {formData.quotations.map(qId => {
                        const q = quotes.find(quote => quote._id === qId);
                        return q ? (
                          <span key={qId} className="badge bg-light text-dark border p-2 fw-500">
                            <i className="bi bi-file-earmark-text me-1"></i> {q.quotationNumber}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">SALES DATE</label>
                    <input type="date" className="form-control" value={formData.sales_date} {...(isEdit && {disabled: true})} onChange={e => setFormData({...formData, sales_date: e.target.value})} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-600">START DATE</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.start_date}
                      onChange={e => {
                        const nextStartDate = e.target.value;
                        const updatedFormData = { ...formData, start_date: nextStartDate };
                        if (shouldLockExpiryDate) {
                          updatedFormData.next_renewal_date = getHundredYearsLater(nextStartDate);
                        } else {
                          const quoteTenureMonths = resolveQuoteTenureMonths(formData.quotation_id, selectedQuote);
                          if (quoteTenureMonths && !expiryChanged) {
                            updatedFormData.next_renewal_date = addMonths(nextStartDate, quoteTenureMonths);
                          }
                        }
                        setFormData(updatedFormData);
                      }}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    {shouldLockExpiryDate ? (
                      <div className="p-2 bg-light rounded-3 d-flex align-items-center" style={{ height: '38px' }}>
                        <span className="text-xs text-muted">
                          <i className="bi bi-infinity me-1 text-primary"></i>
                          Non-Renewal Product — No expiry date
                        </span>
                      </div>
                    ) : (
                      <>
                        <label className="form-label text-muted small fw-600">EXPIRY / RENEWAL DATE</label>
                        <input
                          type="date"
                          className={`form-control ${expiryChanged ? 'border-warning' : ''}`}
                          value={formData.next_renewal_date}
                          onChange={e => handleExpiryChange(e.target.value)}
                          required
                        />
                        {expiryChanged && <div className="text-warning text-xs mt-1 fw-600">Expiry changed! Attach new quotation.</div>}
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label text-muted small fw-600 mb-2 d-block">
                    ATTACHMENTS (Optional)
                    {uploadingAttachments && (
                      <span className="text-success fw-500 ms-2 small">
                        <i className="bi bi-arrow-repeat spin me-1"></i>Uploading…
                      </span>
                    )}
                  </label>
                  <div className="mb-2">
                    <input
                      type="file"
                      multiple
                      className="form-control"
                      onChange={handleAttachmentChange}
                      disabled={uploadingAttachments}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
                    />
                  </div>
                  {attachments.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {attachments.map((att, idx) => {
                        const fileName = att.file_name || att.fileName || (att.url || '').split('/').pop() || 'Attachment';
                        const rawSize = att.file_size ?? att.fileSize ?? null;
                        const sizeKB = rawSize != null ? Math.round(rawSize / 1024) : null;
                        return (
                          <div key={att._id ? `${att._id}-${idx}` : idx} className="d-flex justify-content-between align-items-center p-2 bg-light rounded-3">
                            <button
                              type="button"
                              className="text-dark text-decoration-none d-flex align-items-center gap-2 text-xs fw-600 border-0 bg-transparent text-start p-0"
                              style={{ minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
                              onClick={() => handleAttachmentDownload(att)}
                            >
                              <i className="bi bi-paperclip text-primary"></i>
                              <span className="text-truncate">{fileName}</span>
                              {sizeKB !== null && (
                                <span className="text-muted fw-400">
                                  ({sizeKB < 1024 ? `${sizeKB} KB` : `${(sizeKB / 1024).toFixed(1)} MB`})
                                </span>
                              )}
                              <i className="bi bi-download text-muted"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                              onClick={() => removeAttachment(idx)}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-muted fst-italic">No files attached yet.</div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label text-muted small fw-600 mb-0">SERVICE DETAILS</label>
                    <button 
                      type="button" 
                      className={`btn btn-sm ${serviceDetailsEditMode ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                      onClick={() => setServiceDetailsEditMode(v => !v)}
                    >
                      <i className={`bi ${serviceDetailsEditMode ? 'bi-check2' : 'bi-pencil'} me-1`}></i>
                      {serviceDetailsEditMode ? 'Done Editing' : 'Edit'}
                    </button>
                  </div>
                  <textarea 
                    className="form-control" 
                    rows="3" 
                    value={formData.service_details} 
                    disabled={!serviceDetailsEditMode}
                    onChange={e => setFormData({...formData, service_details: e.target.value})} 
                    placeholder="Technical details, server info, etc."
                  ></textarea>
                </div>
              </div>
            </div>

            {isEdit && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-transparent border-0 pt-4 px-4">
                  <h6 className="fw-700 mb-0">Update & Comments</h6>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">STATUS</label>
                      <select className="form-select" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">ADD COMMENT</label>
                      <textarea className="form-control" rows="2" value={formData.comment} onChange={e => setFormData({...formData, comment: e.target.value})} placeholder="Add a note about this update..."></textarea>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary px-5 py-2 fw-600" disabled={loading}>
                {loading ? 'Processing...' : (isEdit ? 'Update Subscription' : 'Create Subscription')}
              </button>
              <button type="button" className="btn btn-light px-4" onClick={() => navigate(-1)}>Cancel</button>
            </div>
          </form>
        </div>

        <div className="col-md-4">
          {(selectedQuote || (isEdit && formData.quotation_id)) && (
            <div className="card border-0 shadow-sm sticky-top mb-4" style={{ top: '20px' }}>
              <div className="card-header bg-transparent border-0 pt-4 px-4">
                <h6 className="fw-700 mb-0">Selected Quotation Summary</h6>
              </div>
              <div className="card-body p-4">
                {(() => {
                  const quote = selectedQuote || quotes.find(q => q._id === formData.quotation_id);
                  if (!quote) return null;
                  return (
                    <>
                      <div className="mb-4">
                        <div className="text-xs text-muted mb-1">QUOTE NUMBER</div>
                        <div className="fw-700">{quote.quotationNumber}</div>
                      </div>
                      <div className="mb-4">
                        <div className="text-xs text-muted mb-1">ITEMS</div>
                        {quote.items?.map((item, idx) => (
                          <div key={idx} className="d-flex justify-content-between text-xs mb-1">
                            <span>{item.productName} x {item.quantity}</span>
                            <span>{formatCurrency(item.total)}</span>
                          </div>
                        ))}
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between mb-2 small">
                        <span className="text-muted">Sub Total:</span>
                        <span>{formatCurrency(quote.subTotal)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2 small">
                        <span className="text-muted">Tax:</span>
                        <span>{formatCurrency(quote.cgstTotal + quote.sgstTotal)}</span>
                      </div>
                      <div className="d-flex justify-content-between mt-3 p-3 bg-light rounded-3">
                        <span className="fw-700">Total Amount:</span>
                        <span className="fw-700 text-primary">{formatCurrency(quote.totalAmount)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {isEdit && subscription && (
            <>
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-transparent border-0 pt-4 px-4">
                  <h6 className="fw-700 mb-0">Update History</h6>
                </div>
                <div className="card-body p-4">
                  <div className="timeline-sm overflow-auto pe-1" style={{ maxHeight: '420px' }}>
                    {subscription.history?.length > 0 ? subscription.history.slice().reverse().map((h, i) => (
                      <div key={i} className="mb-3 pb-3 border-bottom last-child-no-border">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-xs fw-700">{h.updated_by}</span>
                          <span className="text-xs text-muted">{formatDate(h.updated_at)}</span>
                        </div>
                        <div className="text-xs text-muted mb-1">
                          Status: <span className="text-primary fw-600">{getHistoryStatus(h)}</span>
                        </div>
                        {(h.new_value?.service_details || h.service_details) && (
                          <div className="text-xs text-muted mb-1">
                            Details: <span className="text-dark">{h.new_value?.service_details || h.service_details}</span>
                          </div>
                        )}
                        {h.comment && (
                          <div className="text-xs text-muted italic">
                            Comment: <span className="text-info">"{h.comment}"</span>
                          </div>
                        )}
                      </div>
                    )) : <div className="text-muted text-xs">No history recorded yet.</div>}
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-0 pt-4 px-4">
                  <h6 className="fw-700 mb-0">Recent Comments</h6>
                </div>
                <div className="card-body p-4">
                  {subscription.comments?.length > 0 ? subscription.comments.slice().reverse().map((c, i) => (
                    <div key={i} className="mb-3 p-3 bg-light rounded-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-xs fw-700">{c.admin}</span>
                        <span className="text-xs text-muted">{formatDate(c.date)}</span>
                      </div>
                      <div className="text-xs">{c.text}</div>
                    </div>
                  )) : <div className="text-muted text-xs">No comments yet.</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionForm;
